import { lbBrand } from '@fuse/colors';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import FuseUtils from '@fuse/utils';
import Icon from '@material-ui/core/Icon';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { navbarCloseMobile } from 'app/store/fuse/navbarSlice';
import IconLB from 'app/main/design-system/components/icons/Icon';
import FuseNavBadge from '../FuseNavBadge';


const useStyles = makeStyles(theme => ({
  item: props => ({
    height: 40,
    width: 'calc(100% - 16px)',
    borderRadius: '0 20px 20px 0',
    paddingRight: 12,
    paddingLeft: props.itemPadding > 80 ? 80 : props.itemPadding,
    '&.active': {
      backgroundColor: props.activeBg,
      color: `${lbBrand.white}!important`,
      pointerEvents: 'none',
      transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
      '& .list-item-text-primary': {
        color: 'inherit',
      },
      '& .list-item-icon': {
        color: lbBrand.white,
      },
      '& .list-item-icon div': {
        color: lbBrand.white,
      },
    },
    '&:hover': {
      color: lbBrand.navHover,
      '& .list-item-icon': {
        color: lbBrand.navHover,
      },
      '& .list-item-icon div': {
        color: lbBrand.navHover,
      },
    },
    '& .list-item-icon': {
      color: lbBrand.navInactive,
    },
    '& .list-item-icon div': {
      color: lbBrand.navInactive,
    },
    '& .list-item-text': {},
    color: lbBrand.navInactive,
    cursor: 'pointer',
    textDecoration: 'none!important',
  }),
  iconWrapper: {
    position: 'relative',
    display: 'inline-flex',
    marginRight: 16,
  },
  iconCounter: {
    position: 'absolute',
    top: -6,
    right: -10,
    minWidth: 16,
    height: 16,
    padding: '0 4px',
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '16px',
    textAlign: 'center',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
}));

function FuseNavVerticalItem(props) {
  const userRole = useSelector(({ auth }) => auth.user.role);
  const dispatch = useDispatch();

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { item, nestedLevel } = props;
  const classes = useStyles({
    itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24,
    activeBg: item.activeBg ?? lbBrand.red,
  });
  const { t } = useTranslation();

  const hasPermission = useMemo(() => FuseUtils.hasPermission(item.auth, userRole), [item.auth, userRole]);

  if (!hasPermission) {
    return null;
  }

  return (
    <ListItem
      button
      component={NavLinkAdapter}
      to={item.url}
      activeClassName="active"
      className={clsx(classes.item, 'list-item')}
      onClick={() => mdDown && dispatch(navbarCloseMobile())}
      exact={item.exact}
    >
      {item.icon && (
        <span className={classes.iconWrapper}>
          {item.icon.source === 'FontAwesome' ? (
            <div className="list-item-icon text-16 flex-shrink-0">
              <IconLB source={item.icon.source} name={item.icon.name} size={18} />
            </div>
          ) : (
            <Icon className="list-item-icon text-16 flex-shrink-0" color="action">
              {item.icon.name}
            </Icon>
          )}
          {item.badge?.iconTitle && (
            <span
              className={clsx(classes.iconCounter, 'item-icon-counter')}
              style={{ backgroundColor: item.badge.bg, color: item.badge.fg }}
            >
              {item.badge.iconTitle}
            </span>
          )}
        </span>
      )}

      <ListItemText
        className="list-item-text"
        primary={item.translate ? t(`navigation.${item.translate}`) : item.title}
        classes={{ primary: 'text-14 list-item-text-primary' }}
      />

      {item.badge?.title && <FuseNavBadge badge={item.badge} />}
    </ListItem>
  );
}

FuseNavVerticalItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.shape({
      name: PropTypes.string,
      source: PropTypes.string,
    }),
    url: PropTypes.string,
  }),
};

FuseNavVerticalItem.defaultProps = {};

const NavVerticalItem = withRouter(React.memo(FuseNavVerticalItem));

export default NavVerticalItem;
